import {
  GetTeamProjectsDetailsQuery,
  GetUserAuthMethodsQuery,
  GetUserDetailsQuery,
  ProductName,
  ProjectRolesUpdate,
  urqlGql,
} from '@pypestream/api-services/urql';
import { assign } from '@xstate/immer';
import { resolveSettingsForSave } from './resolve-settings-for-save';
import { compact, isEqual, orderBy, sortBy } from 'lodash';
import { ManagerContext, ManagerEvents } from './manager.xstate.types';
import { filterProjectRolesUpdateByOrgAccess } from './org-access-helper';
import { productsSortFunction } from './permission-details-xstate-helper';
import {
  AccountProject,
  AccountRole,
  ShortOrgType,
  ProjectProductRoles,
} from '../components/permissions-table/permissions-table.types';
import { metabaseAccessPayloadToRows } from '../components/permissions-table/metabase-access/metabase-access.types';
import { ConsentStatusEnum } from '@pypestream/api-services';

// #region Types
export type OrgRowSpanCalculatorType = {
  assignedProjects: ShortOrgType['assignedProjects'];
  assignedProjectIds: string[];
  orgRowSpan: number;
};

export type UserDetailUpdates = {
  hasPendingUpdates: boolean;
  // Project level roles
  projectRolesUpdate?: ProjectRolesUpdate[];
  // Org level updates
  roleIdsToAdd?: string[];
  roleIdsToRemove?: string[];
  teamIdsToAdd?: string[];
  teamIdsToRemove?: string[];
  projectIdsToAdd?: string[];
  projectIdsToRemove?: string[];
  settings?: {
    agentConversationLimit?: number | null;
  };
};

export const userDetailUpdateInitialState: UserDetailUpdates = {
  hasPendingUpdates: false,
  projectRolesUpdate: [],
  roleIdsToAdd: [],
  roleIdsToRemove: [],
  teamIdsToAdd: [],
  teamIdsToRemove: [],
  projectIdsToAdd: [],
  projectIdsToRemove: [],
};

export type UserDetailsType = Pick<
  NonNullable<GetUserDetailsQuery['tenancy_users_by_pk']>,
  | 'id'
  | 'firstName'
  | 'lastName'
  | 'userName'
  | 'picture'
  | 'settings'
  | 'requiredConsentStatus'
  | 'optionalConsentStatus'
  | 'files'
> & {
  orgs: ShortOrgType[];
  canManageAccess: boolean;
  userAuthMethods: NonNullable<
    NonNullable<
      NonNullable<GetUserAuthMethodsQuery['admin_']>['userById']
    >['listOfAuthMethods']
  >;
};

// #endregion Types

// temporary id string for invite user flow
export const NEW_USER_TEMP_ID = 'new-user';
// #endregion constants

export const processTeamInheritedDetails = ({
  teamDetails,
  selectedUserDetails,
}: {
  teamDetails: GetTeamProjectsDetailsQuery['tenancy_teams_by_pk'];
  selectedUserDetails: UserDetailsType;
}): UserDetailsType => {
  if (!selectedUserDetails) return selectedUserDetails;
  teamDetails?.team_accounts.forEach((team_account) => {
    team_account._team_account_roles.forEach((team_account_role) => {
      selectedUserDetails?.orgs.forEach((org) => {
        const isExistingTeamRole = org.accountRoles?.find(
          (accountRole) =>
            accountRole.role.id === team_account_role.role.id &&
            accountRole.teams &&
            accountRole.teams.length &&
            accountRole.teams[0].team.id === teamDetails.id
        );
        const isTeamAppliesToOrg =
          team_account.accountId === org.id ||
          org.allParentIds?.includes(team_account.accountId);
        if (!isExistingTeamRole && isTeamAppliesToOrg) {
          org.accountRoles?.push({
            role: team_account_role.role,
            teams: [
              {
                team: {
                  id: teamDetails.id,
                  name: teamDetails.name,
                  team_accounts: teamDetails.team_accounts,
                },
              },
            ],
          });
          org.accountRoles = sortBy(org.accountRoles, 'teams');
        }
      });
    });

    team_account.team_account_projects.forEach((team_account_project) => {
      const projectOrg = selectedUserDetails.orgs.find(
        (org) => org.id === team_account_project.project.accountId
      );
      if (!projectOrg) return;

      const existingProjectIndex = (
        projectOrg.assignedProjects || []
      ).findIndex(
        (assignedProject) =>
          assignedProject.project.id === team_account_project.project.id
      );

      if (existingProjectIndex >= 0) {
        const existingProject =
          projectOrg.assignedProjects![existingProjectIndex];

        team_account_project._team_account_project_roles.forEach(
          (_team_account_project_role) => {
            const existingInheritedTeamIndex =
              existingProject._account_project_roles.findIndex(
                (_user_account_project_role) =>
                  _user_account_project_role.role.id ===
                    _team_account_project_role.role.id &&
                  !!_user_account_project_role.teams
              );

            if (existingInheritedTeamIndex >= 0) {
              existingProject._account_project_roles[
                existingInheritedTeamIndex
              ].teams?.push({
                team: {
                  id: teamDetails.id,
                  name: teamDetails.name,
                  team_accounts: teamDetails.team_accounts,
                },
              });
            } else {
              existingProject._account_project_roles.push({
                role: _team_account_project_role.role,
                teams: [
                  {
                    team: {
                      id: teamDetails.id,
                      name: teamDetails.name,
                      team_accounts: teamDetails.team_accounts,
                    },
                  },
                ],
              });
            }
            existingProject._account_project_roles = sortBy(
              existingProject._account_project_roles,
              'teams'
            );
          }
        );
      } else {
        const availableProjectRoles =
          team_account_project.project.project_product_settings?.reduce(
            (allProductRoles, currentProduct) => {
              projectOrg.roles?.rows
                ?.filter((role) => role.productId === currentProduct.product.id)
                .forEach((orgProductRole) => {
                  allProductRoles?.push({
                    id: orgProductRole.id,
                    name: orgProductRole.name,
                    productId: orgProductRole.productId,
                    product: orgProductRole.product,
                  });
                });
              return allProductRoles;
            },
            [] as ProjectProductRoles
          );
        const projectToAdd: AccountProject = {
          id: team_account_project.project.id,

          availableProjectRoles: availableProjectRoles,
          _account_project_roles:
            team_account_project._team_account_project_roles.map(
              (_team_account_project_role) => ({
                role: _team_account_project_role.role,
                teams: [
                  {
                    team: {
                      id: teamDetails.id,
                      name: teamDetails.name,
                      team_accounts: teamDetails.team_accounts,
                    },
                  },
                ],
              })
            ),
          project: {
            id: team_account_project.project.id,
            name: team_account_project.project.name,
            accountId: team_account_project.project.accountId,
            project_product_settings:
              team_account_project.project.project_product_settings,
          },
        };

        projectToAdd.project.project_product_settings.sort(
          productsSortFunction
        );

        // Add project to org object's assignedProjects property.
        projectOrg.assignedProjects?.push(projectToAdd);

        // Sort assignedProjects by project name
        projectOrg.assignedProjects = orderBy(
          projectOrg.assignedProjects,
          (assignedProject) => assignedProject.project.name.toLowerCase(),
          'asc'
        );
        // Remove project from org object's availableProjects property.
        projectOrg.availableProjects = projectOrg.availableProjects?.filter(
          (availableProject) =>
            availableProject.id !== team_account_project.project?.id
        );

        // Recalculate rowSpan property for org.
        const canAddMoreProjects =
          (projectOrg.availableProjects?.length || 0) > 0;

        projectOrg.orgRowSpan =
          (projectOrg.orgRowSpan || 1) +
          Math.max(
            projectToAdd.project.project_product_settings.length - 1,
            0
          ) +
          ((canAddMoreProjects && 1) || 0);
      }
    });
  });
  return selectedUserDetails;
};

export const updateManagerRole = assign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (
      event.type !== 'manager.userDetails.updateManagerRole' ||
      !ctx.selectedUser
    )
      return;

    const { orgId, role: roleToUpdate, operation } = event;
    const orgToUpdate = ctx.userDetails[ctx.selectedUser!]?.state?.orgs.find(
      (org) => org.id === orgId
    );
    if (!orgToUpdate || !orgToUpdate.accountRoles) return;

    const selectedUserDetailUpdates =
      ctx.userDetails[ctx.selectedUser!]!.userDetailUpdates;

    if (orgToUpdate) {
      if (operation === 'add') {
        orgToUpdate.accountRoles?.push(roleToUpdate);
        orgToUpdate.availableAccountRoles =
          orgToUpdate.availableAccountRoles?.filter(
            (availableRole) => availableRole.role.id !== roleToUpdate.role.id
          );

        if (
          selectedUserDetailUpdates.roleIdsToRemove?.includes(
            roleToUpdate.role.id
          )
        ) {
          selectedUserDetailUpdates.roleIdsToRemove =
            selectedUserDetailUpdates.roleIdsToRemove?.filter(
              (roleId) => roleId !== roleToUpdate.role.id
            );
        } else {
          selectedUserDetailUpdates.roleIdsToAdd?.push(roleToUpdate.role.id);
        }
      } else {
        const { directlyAssignedAccountRoles, inheritedAccountRoles } =
          orgToUpdate.accountRoles.reduce<{
            directlyAssignedAccountRoles: AccountRole[];
            inheritedAccountRoles: AccountRole[];
          }>(
            (accumulatedRoles, currentRole) => {
              if (currentRole.teams) {
                accumulatedRoles.inheritedAccountRoles.push(currentRole);
              } else {
                accumulatedRoles.directlyAssignedAccountRoles.push(currentRole);
              }
              return accumulatedRoles;
            },
            {
              directlyAssignedAccountRoles: [],
              inheritedAccountRoles: [],
            }
          );

        const roleToRemove =
          directlyAssignedAccountRoles &&
          directlyAssignedAccountRoles.length > 0 &&
          directlyAssignedAccountRoles[0];

        orgToUpdate.accountRoles = inheritedAccountRoles;

        if (operation === 'update' && roleToRemove) {
          orgToUpdate.accountRoles?.push(roleToUpdate);
          orgToUpdate.availableAccountRoles =
            orgToUpdate.availableAccountRoles?.filter(
              (availableRole) => availableRole.role.id !== roleToUpdate.role.id
            );
          orgToUpdate.availableAccountRoles?.push({
            role: {
              id: roleToRemove.role.id,
              name: roleToRemove.role.name,
              productId: roleToRemove.role.productId,
              product: {
                id: roleToRemove.role.productId,
                name: roleToRemove.role.product?.name,
                displayName: roleToRemove.role.product?.displayName,
              },
              accountId: roleToRemove.role.accountId,
            },
          });

          if (
            isEqual(selectedUserDetailUpdates.roleIdsToAdd, [
              roleToRemove.role.id,
            ]) &&
            isEqual(selectedUserDetailUpdates.roleIdsToRemove, [
              roleToUpdate.role.id,
            ])
          ) {
            // If updating roles, going back to default state, then ignore the updates with selectedUserDetailUpdates
            selectedUserDetailUpdates.roleIdsToAdd = [];
            selectedUserDetailUpdates.roleIdsToRemove = [];
          } else {
            selectedUserDetailUpdates.roleIdsToAdd?.push(roleToUpdate.role.id);
            selectedUserDetailUpdates.roleIdsToRemove?.push(
              roleToRemove.role.id
            );

            if (
              selectedUserDetailUpdates.roleIdsToAdd?.includes(
                roleToRemove.role.id
              )
            ) {
              selectedUserDetailUpdates.roleIdsToRemove =
                selectedUserDetailUpdates.roleIdsToRemove?.filter(
                  (id) => id !== roleToRemove.role.id
                );
            }

            selectedUserDetailUpdates.roleIdsToAdd =
              selectedUserDetailUpdates.roleIdsToAdd?.filter(
                (id) => id !== roleToRemove.role.id
              );
            selectedUserDetailUpdates.roleIdsToRemove =
              selectedUserDetailUpdates.roleIdsToRemove?.filter(
                (id) => id !== roleToUpdate.role.id
              );
          }
        } else if (operation === 'remove') {
          orgToUpdate.availableAccountRoles?.push({
            role: {
              id: roleToUpdate.role.id || '',
              name: roleToUpdate.role.name || '',
              productId: roleToUpdate.role.productId || '',
              product: {
                id: roleToUpdate.role.productId || '',
                name: roleToUpdate.role.product?.name || '',
                displayName: roleToUpdate.role.product?.displayName || '',
              },
              accountId: roleToUpdate.role.accountId || '',
            },
          });

          if (
            selectedUserDetailUpdates.roleIdsToAdd?.includes(
              roleToUpdate.role.id
            )
          ) {
            selectedUserDetailUpdates.roleIdsToAdd =
              selectedUserDetailUpdates.roleIdsToAdd?.filter(
                (roleId) => roleId !== roleToUpdate.role.id
              );
          } else {
            selectedUserDetailUpdates.roleIdsToRemove?.push(
              roleToUpdate.role.id
            );
          }
        }
      }

      orgToUpdate.availableAccountRoles = sortBy(
        orgToUpdate.availableAccountRoles,
        'role.name'
      );

      // We need to update the accountRoles for all orgs, as accountRoles cascade down
      ctx.userDetails[ctx.selectedUser!]!.state!.orgs = ctx.userDetails[
        ctx.selectedUser!
      ]!.state!.orgs.map((org) => ({
        ...org,
        accountRoles: orgToUpdate.accountRoles,
      }));
    }

    ctx.userDetails[ctx.selectedUser!]!.userDetailUpdates =
      selectedUserDetailUpdates;
    ctx.userDetails[ctx.selectedUser!].userDetailUpdates.hasPendingUpdates =
      hasPendingUpdates(ctx.userDetails[ctx.selectedUser!]);
  }
);

export const handleAddProjectAccess = assign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (event.type !== 'manager.userDetails.addProject') return;
    const { project } = event;
    // @todo - fix usage of selectedUser. it should be present by default.
    if (!ctx.selectedUser) return ctx;
    const { userDetailUpdates } = ctx.userDetails[ctx.selectedUser]!;

    if (userDetailUpdates.projectIdsToRemove?.includes(project.id)) {
      userDetailUpdates.projectIdsToRemove =
        userDetailUpdates.projectIdsToRemove?.filter((id) => id !== project.id);
    } else {
      userDetailUpdates.projectIdsToAdd?.push(project.id);
    }
    const allOrgs = ctx.userDetails?.[ctx.selectedUser!]?.state?.orgs || [];

    const orgIndexToUpdate = allOrgs.findIndex(
      (org) => org.id === project.accountId
    );

    const orgAvailableRoleDetails =
      ctx.userDetails?.[ctx.selectedUser]?.state?.orgs[orgIndexToUpdate]?.roles
        ?.rows;

    if (orgIndexToUpdate >= 0) {
      const { projectProductSettings } = project;
      // Recreating project_product_settings object to align types with 2 different sources.
      const project_product_settings: AccountProject['project']['project_product_settings'] =
        [];

      // Calculate all available roles for the project
      const availableProjectRoles = projectProductSettings?.reduce(
        (allProductRoles, currentProduct) => {
          orgAvailableRoleDetails
            ?.filter((role) => role.productId === currentProduct.productId)
            .forEach((orgProductRole) => {
              allProductRoles?.push({
                id: orgProductRole.id,
                name: orgProductRole.name,
                productId: orgProductRole.productId,
                product: orgProductRole.product,
              });
            });

          // Aligning types with 2 different sources of project_product_settings.
          project_product_settings.push({
            settings: currentProduct.settings,
            product: {
              name: currentProduct.product?.name || '',
              id: currentProduct.product?.id || '',
              displayName: currentProduct.product?.displayName || '',
            },
          });

          return allProductRoles;
        },
        [] as ProjectProductRoles
      );

      project_product_settings.sort(productsSortFunction);

      const projectToAdd: AccountProject = {
        id: project.id,
        availableProjectRoles,
        _account_project_roles: [],
        project: {
          id: project.id,
          name: project.name,
          accountId: project.accountId,
          project_product_settings,
        },
      };

      // Add project to org object's assignedProjects property.
      allOrgs[orgIndexToUpdate].assignedProjects?.push(projectToAdd);

      // Remove project from org object's availableProjects property.
      allOrgs[orgIndexToUpdate].availableProjects = allOrgs[
        orgIndexToUpdate
      ].availableProjects?.filter(
        (availableProject) => availableProject.id !== project.id
      );

      // Recalculate rowSpan property for org.
      const canAddMoreProjects =
        (allOrgs[orgIndexToUpdate].availableProjects?.length || 0) > 0;

      allOrgs[orgIndexToUpdate].orgRowSpan =
        (allOrgs[orgIndexToUpdate].orgRowSpan || 1) +
        Math.max(projectToAdd.project.project_product_settings.length - 1, 0) +
        ((canAddMoreProjects && 1) || 0);
    }

    userDetailUpdates.hasPendingUpdates = hasPendingUpdates(
      ctx.userDetails[ctx.selectedUser]
    );
    return ctx;
  }
);

export const removeTeam = assign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (event.type !== 'manager.userDetails.removeTeam' || !ctx.selectedUser)
      return;
    const { orgId, team: teamToRemove } = event;
    const selectUserDetails = ctx.userDetails[ctx.selectedUser];

    const orgToUpdate = selectUserDetails?.state?.orgs.find(
      (org) => org.id === orgId
    );

    if (orgToUpdate) {
      const teamIndexToRemove = (orgToUpdate.assignedTeams || []).findIndex(
        (team) => team.team.id === teamToRemove.team.id
      );

      if (
        teamIndexToRemove >= 0 &&
        orgToUpdate.assignedTeams &&
        orgToUpdate.assignedTeams[teamIndexToRemove]
      ) {
        orgToUpdate.availableTeams?.push({
          id: orgToUpdate.assignedTeams?.[teamIndexToRemove]?.team?.id,
          name: orgToUpdate.assignedTeams?.[teamIndexToRemove]?.team?.name,
          assignedProjects: orgToUpdate.assignedTeams[
            teamIndexToRemove
          ]?.team?.team_accounts[0].team_account_projects.map(
            (team_account_project) => ({
              id: team_account_project.project.id,
              name: team_account_project.project.name,
              accountId: team_account_project.project.accountId,
            })
          ),
          assignedRoles: orgToUpdate.assignedTeams[
            teamIndexToRemove
          ]?.team?.team_accounts[0]._team_account_roles.map(
            (_team_account_role) => ({
              id: _team_account_role.role.id,
              name: _team_account_role.role.name,
              productId: _team_account_role.role.productId,
              product: {
                name: (_team_account_role.role.product?.name ||
                  '') as ProductName,
                id: _team_account_role.role.product?.id || '',
                displayName: _team_account_role.role.product?.displayName || '',
              },
            })
          ),
        });

        orgToUpdate.availableTeams = sortBy(orgToUpdate.availableTeams, 'name');

        orgToUpdate.assignedTeams?.splice(teamIndexToRemove, 1);
      }

      ctx.userDetails[ctx.selectedUser]!.state!.orgs = ctx.userDetails[
        ctx.selectedUser
      ]!.state!.orgs.map((org) => {
        org.accountRoles = compact(
          org.accountRoles?.map((accountRole) => {
            accountRole.teams = accountRole.teams?.filter(
              (team) => team.team.id !== teamToRemove.team.id
            );
            if (accountRole.teams?.length === 0) {
              return null;
            }
            return accountRole;
          })
        );
        return org;
      });
      teamToRemove.team.team_accounts[0].team_account_projects.forEach(
        (team_account_project) => {
          const orgToUpdateForInheritedProjectRoles =
            selectUserDetails?.state?.orgs.find(
              (org) => org.id === team_account_project.project.accountId
            );
          if (orgToUpdateForInheritedProjectRoles) {
            const projectToUpdate =
              orgToUpdateForInheritedProjectRoles?.assignedProjects?.find(
                (assignedProject) =>
                  assignedProject.project.id === team_account_project.project.id
              );
            if (projectToUpdate) {
              projectToUpdate._account_project_roles = sortBy(
                projectToUpdate._account_project_roles.filter(
                  (_user_account_project_role) =>
                    !_user_account_project_role.teams ||
                    _user_account_project_role.teams.find(
                      (team) => team.team.id !== teamToRemove.team.id
                    )
                ),
                'teams'
              );
            }
          }
        }
      );
    }

    if (!selectUserDetails?.userDetailUpdates.teamIdsToRemove) {
      selectUserDetails.userDetailUpdates.teamIdsToRemove = [];
    }

    if (
      !(selectUserDetails?.userDetailUpdates.teamIdsToAdd || []).includes(
        teamToRemove.team.id
      )
    ) {
      (selectUserDetails?.userDetailUpdates.teamIdsToRemove || []).push(
        teamToRemove.team.id
      );
    } else {
      selectUserDetails.userDetailUpdates.teamIdsToAdd = (
        selectUserDetails?.userDetailUpdates.teamIdsToAdd || []
      ).filter((teamId) => teamId !== teamToRemove.team.id);
    }

    selectUserDetails.userDetailUpdates.hasPendingUpdates =
      hasPendingUpdates(selectUserDetails);

    return ctx;
  }
);

export const removeProjectWithRoles = assign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (
      event.type !== 'manager.userDetails.removeProjectWithRoles' ||
      !ctx.selectedUser
    )
      return;

    const { projectId, orgId } = event;
    const orgToUpdate = ctx.userDetails[ctx.selectedUser]?.state?.orgs.find(
      (org) => org.id === orgId
    );
    const projectToUpdate = orgToUpdate?.assignedProjects?.find(
      (project) => project.project.id === projectId
    );
    const selectedUserDetailUpdates =
      ctx.userDetails[ctx.selectedUser]!.userDetailUpdates;
    const existingProjectRoleUpdate =
      selectedUserDetailUpdates.projectRolesUpdate;
    const existingProjectRoleUpdateIndex = (
      selectedUserDetailUpdates.projectRolesUpdate || []
    ).findIndex((_projectRoleUpdate) => _projectRoleUpdate.id === projectId);

    if (!orgToUpdate || !projectToUpdate || !existingProjectRoleUpdate)
      return ctx;

    const projectRoleUpdate: ProjectRolesUpdate = {
      id: projectId,
      rolesToRemove:
        (existingProjectRoleUpdateIndex >= 0 &&
          selectedUserDetailUpdates.projectRolesUpdate?.[
            existingProjectRoleUpdateIndex
          ]?.rolesToRemove) ||
        [],
      rolesToAdd:
        (existingProjectRoleUpdateIndex >= 0 &&
          selectedUserDetailUpdates.projectRolesUpdate?.[
            existingProjectRoleUpdateIndex
          ]?.rolesToAdd) ||
        [],
    };

    const hadDirectRoles = projectToUpdate._account_project_roles.some(
      (account_project_role) => !account_project_role.teams
    );

    projectToUpdate._account_project_roles.forEach((account_project_role) => {
      if (account_project_role.teams) return;

      if (
        projectRoleUpdate.rolesToAdd?.includes(account_project_role.role.id)
      ) {
        projectRoleUpdate.rolesToAdd = projectRoleUpdate.rolesToAdd?.filter(
          (roleId) => roleId !== account_project_role.role.id
        );
      } else if (
        !projectRoleUpdate.rolesToRemove?.includes(account_project_role.role.id)
      ) {
        projectRoleUpdate.rolesToRemove?.push(account_project_role.role.id);
      }

      projectToUpdate.availableProjectRoles?.push({
        id: account_project_role.role.id,
        name: account_project_role.role.name || '',
        productId: account_project_role.role.productId || '',
        product: {
          id: account_project_role.role.product?.id || '',
          name: (account_project_role.role.product?.name || '') as ProductName,
          displayName: account_project_role.role.product?.displayName || '',
        },
      });
    });

    projectToUpdate._account_project_roles = [];
    projectRoleUpdate.rolesToAdd = [];
    orgToUpdate.assignedProjects = orgToUpdate.assignedProjects?.filter(
      (project) => project.project.id !== projectId
    );

    if (selectedUserDetailUpdates.projectIdsToAdd?.includes(projectId)) {
      selectedUserDetailUpdates.projectIdsToAdd =
        selectedUserDetailUpdates.projectIdsToAdd?.filter(
          (id) => id !== projectId
        );
    } else if (hadDirectRoles) {
      selectedUserDetailUpdates.projectIdsToRemove?.push(projectId);
    }

    const canAddMoreProjects = (orgToUpdate.availableProjects?.length || 0) > 0;

    orgToUpdate.availableProjects?.push({
      accountId: projectToUpdate.project.accountId,
      id: projectToUpdate.project.id,
      name: projectToUpdate.project.name,
      projectProductSettings:
        projectToUpdate.project.project_product_settings.map(
          (projectProduct) => ({
            settings: projectProduct.settings,
            product: {
              displayName: projectProduct.product.displayName,
              name: projectProduct.product.name as ProductName,
              id: projectProduct.product.id,
            },
            productId: projectProduct.product.id,
          })
        ),
    });
    orgToUpdate.availableProjects = sortBy(
      orgToUpdate.availableProjects,
      'name'
    );

    if (existingProjectRoleUpdateIndex >= 0) {
      existingProjectRoleUpdate[existingProjectRoleUpdateIndex] =
        projectRoleUpdate;
    } else {
      existingProjectRoleUpdate.push(projectRoleUpdate);
    }

    selectedUserDetailUpdates.projectRolesUpdate =
      existingProjectRoleUpdate.filter(
        (roleUpdate) =>
          (roleUpdate.rolesToAdd || []).length > 0 ||
          (roleUpdate.rolesToRemove || []).length > 0
      );

    orgToUpdate.orgRowSpan = Math.max(
      (orgToUpdate.orgRowSpan || 1) -
        Math.max(projectToUpdate.project.project_product_settings.length, 0) +
        (canAddMoreProjects ? 0 : 1),
      1
    );

    selectedUserDetailUpdates.hasPendingUpdates = hasPendingUpdates(
      ctx.userDetails[ctx.selectedUser]
    );

    return ctx;
  }
);

export const updateProjectProductRole = assign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (
      event.type !== 'manager.userDetails.updateProjectProductRole' ||
      !ctx.selectedUser
    )
      return;
    const { projectId, orgId, role, operation } = event;
    const orgToUpdate = ctx.userDetails[ctx.selectedUser]?.state?.orgs.find(
      (org) => org.id === orgId
    );
    const projectToUpdate = orgToUpdate?.assignedProjects?.find(
      (project) => project.project.id === projectId
    );
    const selectedUserProjectRoleUpdate =
      ctx.userDetails[ctx.selectedUser]!.userDetailUpdates;
    const existingProjectRoleUpdateIndex = (
      selectedUserProjectRoleUpdate.projectRolesUpdate || []
    ).findIndex((_projectRoleUpdate) => _projectRoleUpdate.id === projectId);

    const projectRoleUpdate: ProjectRolesUpdate = {
      id: projectId,
      rolesToRemove:
        (existingProjectRoleUpdateIndex >= 0 &&
          selectedUserProjectRoleUpdate.projectRolesUpdate?.[
            existingProjectRoleUpdateIndex
          ]?.rolesToRemove) ||
        [],
      rolesToAdd:
        (existingProjectRoleUpdateIndex >= 0 &&
          selectedUserProjectRoleUpdate.projectRolesUpdate?.[
            existingProjectRoleUpdateIndex
          ]?.rolesToAdd) ||
        [],
      metabaseAccess:
        (existingProjectRoleUpdateIndex >= 0 &&
          selectedUserProjectRoleUpdate.projectRolesUpdate?.[
            existingProjectRoleUpdateIndex
          ]?.metabaseAccess) ||
        undefined,
    };

    if (projectToUpdate) {
      projectToUpdate._account_project_roles.forEach(
        (_user_account_project_role) => {
          if (
            _user_account_project_role.role.productId === role.productId &&
            // Only process non-inherited roles
            (_user_account_project_role.teams || []).length === 0
          ) {
            if (
              !projectToUpdate.availableProjectRoles?.find(
                (availableRole) =>
                  availableRole.id === _user_account_project_role.role.id
              )
            ) {
              projectToUpdate.availableProjectRoles?.push({
                id: _user_account_project_role.role.id,
                name: _user_account_project_role.role.name || '',
                productId: _user_account_project_role.role.productId || '',
                product: {
                  id: _user_account_project_role.role.product?.id || '',
                  name: (_user_account_project_role.role.product?.name ||
                    '') as ProductName,
                  displayName:
                    _user_account_project_role.role.product?.displayName || '',
                },
              });

              if (
                !projectRoleUpdate.rolesToAdd?.includes(
                  _user_account_project_role.role.id
                )
              ) {
                projectRoleUpdate.rolesToRemove?.push(
                  _user_account_project_role.role.id
                );
              } else {
                projectRoleUpdate.rolesToAdd =
                  projectRoleUpdate.rolesToAdd?.filter(
                    (roleId) => roleId !== _user_account_project_role.role.id
                  ) || [];
              }
            }
          }
        }
      );

      projectToUpdate._account_project_roles =
        projectToUpdate._account_project_roles.filter(
          (_user_account_project_role) => {
            if ((_user_account_project_role.teams?.length || 0) > 0) {
              return true;
            }
            return _user_account_project_role.role.productId !== role.productId;
          }
        );

      if (operation === 'add' || operation === 'update') {
        projectToUpdate._account_project_roles.push({
          role: {
            id: role.id || '',
            name: role.name || '',
            productId: role.productId || '',
            product: {
              name: role.product?.name || '',
              id: role.product?.id || '',
              displayName: role.product?.displayName || '',
            },
          },
        });
        projectToUpdate.availableProjectRoles =
          projectToUpdate.availableProjectRoles?.filter(
            (availableProjectRole) => availableProjectRole.id !== role.id
          );
      }

      if (operation !== 'remove') {
        if (
          !projectRoleUpdate.rolesToAdd?.includes(role.id || '') &&
          !projectRoleUpdate.rolesToRemove?.includes(role.id || '')
        ) {
          projectRoleUpdate.rolesToAdd?.push(role.id || '');
        } else {
          projectRoleUpdate.rolesToRemove =
            projectRoleUpdate.rolesToRemove?.filter(
              (roleId) => roleId !== role.id
            ) || [];
        }
      }

      projectToUpdate._account_project_roles = sortBy(
        projectToUpdate._account_project_roles,
        'teams'
      );

      const projectRolesUpdateCtx =
        ctx.userDetails[ctx.selectedUser]!.userDetailUpdates.projectRolesUpdate;
      if (!projectRolesUpdateCtx) return;

      if (existingProjectRoleUpdateIndex >= 0) {
        projectRolesUpdateCtx[existingProjectRoleUpdateIndex] =
          projectRoleUpdate;
      } else {
        projectRolesUpdateCtx.push(projectRoleUpdate);
      }

      ctx.userDetails[ctx.selectedUser]!.userDetailUpdates.projectRolesUpdate =
        projectRolesUpdateCtx.filter(
          (roleUpdate) =>
            (roleUpdate.rolesToAdd || []).length > 0 ||
            (roleUpdate.rolesToRemove || []).length > 0 ||
            roleUpdate.metabaseAccess !== undefined
        );

      ctx.userDetails[ctx.selectedUser]!.userDetailUpdates.hasPendingUpdates =
        hasPendingUpdates(ctx.userDetails[ctx.selectedUser]);
    }

    return ctx;
  }
);

export const updateProjectMetabaseAccess = assign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (
      event.type !== 'manager.userDetails.updateProjectMetabaseAccess' ||
      !ctx.selectedUser
    )
      return;

    const { projectId, orgId, productId, metabaseAccess, desiredRoles } = event;
    const { userDetailUpdates } = ctx.userDetails[ctx.selectedUser]!;
    const projectRolesUpdate = userDetailUpdates.projectRolesUpdate || [];
    const existingIndex = projectRolesUpdate.findIndex(
      (entry) => entry.id === projectId
    );

    const entry: ProjectRolesUpdate =
      existingIndex >= 0
        ? { ...projectRolesUpdate[existingIndex] }
        : { id: projectId, rolesToAdd: [], rolesToRemove: [] };
    entry.rolesToAdd = [...(entry.rolesToAdd || [])];
    entry.rolesToRemove = [...(entry.rolesToRemove || [])];

    const projectToUpdate = ctx.userDetails[ctx.selectedUser]?.state?.orgs
      .find((org) => org.id === orgId)
      ?.assignedProjects?.find((project) => project.project.id === projectId);

    if (projectToUpdate) {
      // Direct (non-team) analytics roles are the baseline union to diff against.
      const currentDirectRoles = projectToUpdate._account_project_roles.filter(
        (accountProjectRole) =>
          (accountProjectRole.teams?.length || 0) === 0 &&
          accountProjectRole.role.productId === productId
      );
      const currentIds = currentDirectRoles.map(({ role }) => role.id);
      const desiredIds = desiredRoles.map((role) => role.id || '');

      const toAdd = desiredIds.filter((id) => !currentIds.includes(id));
      const toRemove = currentIds.filter((id) => !desiredIds.includes(id));

      toAdd.forEach((id) => {
        if (entry.rolesToRemove!.includes(id)) {
          entry.rolesToRemove = entry.rolesToRemove!.filter((r) => r !== id);
        } else if (!entry.rolesToAdd!.includes(id)) {
          entry.rolesToAdd!.push(id);
        }
      });
      toRemove.forEach((id) => {
        if (entry.rolesToAdd!.includes(id)) {
          entry.rolesToAdd = entry.rolesToAdd!.filter((r) => r !== id);
        } else if (!entry.rolesToRemove!.includes(id)) {
          entry.rolesToRemove!.push(id);
        }
      });

      const removedRoleObjs = currentDirectRoles.filter(({ role }) =>
        toRemove.includes(role.id)
      );
      projectToUpdate._account_project_roles =
        projectToUpdate._account_project_roles.filter(
          (accountProjectRole) =>
            (accountProjectRole.teams?.length || 0) > 0 ||
            accountProjectRole.role.productId !== productId
        );
      desiredRoles.forEach((role) => {
        projectToUpdate._account_project_roles.push({
          role: {
            id: role.id || '',
            name: role.name || '',
            productId: role.productId || '',
            product: {
              id: role.product?.id || '',
              name: role.product?.name || '',
              displayName: role.product?.displayName || '',
            },
          },
        });
      });
      projectToUpdate._account_project_roles = sortBy(
        projectToUpdate._account_project_roles,
        'teams'
      );

      projectToUpdate.availableProjectRoles = (
        projectToUpdate.availableProjectRoles || []
      ).filter((role) => !desiredIds.includes(role.id || ''));
      removedRoleObjs.forEach(({ role }) => {
        if (
          !projectToUpdate.availableProjectRoles!.find((r) => r.id === role.id)
        ) {
          projectToUpdate.availableProjectRoles!.push({
            id: role.id,
            name: role.name || '',
            productId: role.productId || '',
            product: {
              id: role.product?.id || '',
              name: (role.product?.name || '') as ProductName,
              displayName: role.product?.displayName || '',
            },
          });
        }
      });

      // Optimistically mirror the saved grants so the cell re-renders now.
      projectToUpdate.metabase_access =
        metabaseAccessPayloadToRows(metabaseAccess);
    }

    entry.metabaseAccess = metabaseAccess;

    if (existingIndex >= 0) {
      projectRolesUpdate[existingIndex] = entry;
    } else {
      projectRolesUpdate.push(entry);
    }

    userDetailUpdates.projectRolesUpdate = projectRolesUpdate.filter(
      (roleUpdate) =>
        (roleUpdate.rolesToAdd || []).length > 0 ||
        (roleUpdate.rolesToRemove || []).length > 0 ||
        roleUpdate.metabaseAccess !== undefined
    );

    userDetailUpdates.hasPendingUpdates = hasPendingUpdates(
      ctx.userDetails[ctx.selectedUser]
    );

    return ctx;
  }
);

export const handleUpdateUserDetails = async ({
  userId,
  orgId,
  userDetails,
  firstName,
  lastName,
  optionalConsentStatus,
  userIconId,
  isConsolidatingDetails = false,
}: {
  userId: string;
  orgId: string;
  userDetails: ManagerContext['userDetails'][string];
  firstName?: string;
  lastName?: string;
  optionalConsentStatus?: ConsentStatusEnum;
  userIconId?: string;
  isConsolidatingDetails?: boolean;
}): Promise<{ userId: string; allRoleNamesToAdd: string[] }> => {
  try {
    const projectRolesUpdate: ProjectRolesUpdate[] =
      userDetails.userDetailUpdates.projectRolesUpdate || [];

    const roleIdsToAdd: string[] =
      userDetails.userDetailUpdates.roleIdsToAdd || [];

    const allRoleIdsToAdd = projectRolesUpdate
      .map(({ rolesToAdd }) => rolesToAdd)
      .flat()
      .concat(roleIdsToAdd);

    const allRoleNamesToAdd = allRoleIdsToAdd
      .map((roleId) => {
        return userDetails?.state?.orgs[0]?.roles?.rows?.find(
          ({ id }) => id === roleId
        ) as AccountRole['role'] | undefined;
      })
      .filter((role): role is AccountRole['role'] => role !== undefined)
      .map(({ name }) => name);

    // PSTUDIO-3111: project-role updates can target child orgs the per-org
    // guards allowed even when the route org is not manageable — gate each
    // entry by ITS org's canManageAccess instead of the single route-org
    // flag (which silently dropped legitimate child-org updates).
    const allowedProjectRolesUpdate = filterProjectRolesUpdateByOrgAccess(
      userDetails.state?.orgs,
      projectRolesUpdate
    );

    let projectRoleUpdateDetailsError;
    if (allowedProjectRolesUpdate.length > 0) {
      const { error } = await urqlGql.setUserAccountProjectsRoles({
        userId,
        projectRolesUpdate: allowedProjectRolesUpdate,
        isConsolidatingDetails,
      });
      projectRoleUpdateDetailsError = error;
    }

    const {
      data: userAccountUpdateDetails,
      error: userAccountUpdateDetailsError,
    } = await urqlGql.updateUserAccountRoles({
      id: userId,
      accountId: orgId!,
      firstName: firstName || userDetails.state?.firstName || '',
      lastName: lastName || userDetails.state?.lastName || '',
      optionalConsentStatus,
      userIconId,
      roleIdsToAdd,
      roleIdsToRemove: userDetails.userDetailUpdates.roleIdsToRemove || [],
      teamIdsToAdd: userDetails.userDetailUpdates.teamIdsToAdd || [],
      teamIdsToRemove: userDetails.userDetailUpdates.teamIdsToRemove || [],
      projectIdsToAdd:
        userDetails.userDetailUpdates.projectIdsToAdd?.filter(
          (projectIdToAdd) =>
            userDetails.userDetailUpdates.projectRolesUpdate?.find(
              (projectRoleUpdate) => projectRoleUpdate.id === projectIdToAdd
            )
        ) || [],
      projectIdsToRemove:
        userDetails.userDetailUpdates.projectIdsToRemove || [],
      settings: resolveSettingsForSave(userId, userDetails.state?.settings),
    });

    const updateError =
      projectRoleUpdateDetailsError || userAccountUpdateDetailsError;
    if (updateError) {
      throw new Error(updateError.message);
    }
    return Promise.resolve({
      userId,
      allRoleNamesToAdd,
    });
  } catch (error) {
    throw error instanceof Error ? error : new Error('Something went wrong');
  }
};

export const updateUserSettings = assign(
  (ctx: ManagerContext, event: ManagerEvents) => {
    if (
      event.type !== 'manager.userDetails.updateUserSettings' ||
      !ctx.selectedUser
    ) {
      return;
    }

    const eventSettings = Object.entries(event.settings).reduce(
      (res, [key, value]) => {
        switch (key) {
          case 'agentConversationLimit':
            if (value == null) {
              res[key] = undefined;
            } else {
              res[key] = value;
            }
            break;
          default:
            res[key] = value;
        }

        return res;
      },
      {} as Record<string, unknown>
    );

    ctx.userDetails[ctx.selectedUser]!.state!.settings = {
      ...(ctx.userDetails[ctx.selectedUser]!.state!.settings || {}),
      ...eventSettings,
    };

    ctx.userDetails[ctx.selectedUser]!.userDetailUpdates.hasPendingUpdates =
      hasPendingUpdates(ctx.userDetails[ctx.selectedUser]);

    return ctx;
  }
);

export const hasPendingUpdates = ({
  userDetailUpdates,
  state,
  initialState,
}: {
  userDetailUpdates: UserDetailUpdates;
  state: UserDetailsType | null;
  initialState: UserDetailsType | null;
}): boolean => {
  return (
    (userDetailUpdates.projectRolesUpdate || []).length > 0 ||
    (userDetailUpdates.roleIdsToAdd || []).length > 0 ||
    (userDetailUpdates.roleIdsToRemove || []).length > 0 ||
    (userDetailUpdates.projectIdsToRemove || []).length > 0 ||
    (userDetailUpdates.teamIdsToAdd || []).length > 0 ||
    (userDetailUpdates.teamIdsToRemove || []).length > 0 ||
    // Validate whether we have pending updates for settings
    JSON.stringify(state?.settings) !== JSON.stringify(initialState?.settings)
  );
};
// #endregion - Helper methods
